import { useCallback, useEffect, useMemo, useState } from 'react';

import { Portal } from '@radix-ui/react-portal';

import { useGetCaseForGrade } from '@/hooks/api/use-get-case-for-grade';
import { useInsightsImages } from '@/hooks/grade-flow/use-insights-images';
import { extractBaseImageName, getLateralityFromImageName } from '@/utils/cases';

import { Overlay } from '@/components/ui/overlay';
import { FetchStates } from '@/components/fetch-states';

import { ImageSegDataAccordionSelect } from '../image-seg-data-accordion-select';
import { InsightsActions } from './insight-actions';

type SegmentationModalProps = {
  caseId: string;
  open?: boolean;
  showActions?: boolean;
  filter?: 'all' | 'left' | 'right';
  onOpenChange?: (open: boolean) => void;
  initialImage?: string;
};

export function SegmentationModal({
  caseId,
  open: externalOpen,
  showActions = true,
  filter = 'all',
  onOpenChange,
  initialImage,
}: SegmentationModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | undefined>();
  const [segmentationSelected, setSegmentationSelected] = useState<
    ('vessels' | 'optic_disc' | 'lesions')[] | undefined
  >();

  const isOpen = externalOpen ?? internalOpen;

  const {
    data: _case,
    loading,
    error,
  } = useGetCaseForGrade({ caseId: isOpen ? caseId : undefined });
  const images = _case?.images;
  const {
    layerImages,
    segmentationData,
    loading: loadingLayers,
  } = useInsightsImages({ caseId: isOpen ? caseId : undefined });

  const expandedLayerImages = useMemo(() => {
    if (!layerImages?.length || !expanded) return undefined;

    const expandedName = extractBaseImageName(expanded);
    const filteredLayers = layerImages.filter((i) =>
      extractBaseImageName(i.layer).includes(expandedName)
    );

    const vesselsLayer = filteredLayers.find((i) => i.layer.includes('vessels'));
    const opticDiscLayer = filteredLayers.find((i) => i.layer.includes('optic_disc'));
    const lesionsLayer = filteredLayers.find((i) => i.layer.includes('lesions'));

    return {
      vessels: vesselsLayer,
      optic_disc: opticDiscLayer,
      lesions: lesionsLayer,
    };
  }, [layerImages, expanded]);

  const handleClose = useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalOpen(false);
    }
    setExpanded(undefined);
    setSegmentationSelected(undefined);
  }, [onOpenChange]);

  const handleExpand = useCallback((image: string) => setExpanded(image), []);

  // Filter images based on laterality using getLateralityFromImageName
  const imagesFiltered = useMemo(() => {
    if (filter === 'all') {
      return images ?? [];
    }

    return (
      images?.filter((image) => {
        const laterality = getLateralityFromImageName(image.image);
        return laterality.eyeside === filter;
      }) ?? []
    );
  }, [images, filter]);

  // Set initial expanded image when modal opens
  useEffect(() => {
    if (imagesFiltered?.length && isOpen) {
      if (initialImage && imagesFiltered.some((img) => img.image === initialImage)) {
        setExpanded(initialImage);
      } else {
        setExpanded(imagesFiltered[0].image);
      }
    }
  }, [imagesFiltered, isOpen, initialImage]);

  // Handle case where current expanded image is not in the filtered list (e.g., filter changed)
  useEffect(() => {
    if (
      imagesFiltered?.length &&
      expanded &&
      !imagesFiltered.some((img) => img.image === expanded)
    ) {
      setExpanded(imagesFiltered[0].image);
    }
  }, [imagesFiltered, expanded]);

  return (
    <>
      {isOpen ? (
        <Portal asChild>
          <Overlay open={isOpen} onClose={handleClose} allowScroll>
            <div
              className={
                loading || loadingLayers || error?.message
                  ? 'h-screen mx-auto flex justify-center items-center'
                  : ''
              }
            >
              <FetchStates loading={loading || loadingLayers} error={error?.message}>
                <div className="flex flex-wrap lg:flex-row justify-between items-start py-6 px-4 gap-4">
                  <InsightsActions
                    current={expanded}
                    images={imagesFiltered}
                    setExpanded={handleExpand}
                    patientId={_case?.patient_id}
                    caseId={caseId}
                    showActions={showActions}
                    grades={_case?.grades}
                  />

                  <div className="flex-1 h-auto flex flex-col items-center w-full shrink-0 min-w-[80vw] xl:min-w-0 order-1 xl:order-2">
                    <picture className="relative">
                      <img
                        className="h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] relative"
                        src={imagesFiltered?.find((i) => i.image === expanded)?.url}
                        alt={`Expanded image ${expanded}`}
                      />
                      {/* Overlay images based on segmentation selection */}
                      {segmentationSelected?.includes('vessels') && expandedLayerImages?.vessels ? (
                        <img
                          className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                          src={expandedLayerImages?.vessels.url}
                          alt="Vascular overlay"
                        />
                      ) : null}
                      {segmentationSelected?.includes('optic_disc') &&
                      expandedLayerImages?.optic_disc ? (
                        <img
                          className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                          src={expandedLayerImages.optic_disc.url}
                          alt="Optic disc overlay"
                        />
                      ) : null}
                      {segmentationSelected?.includes('lesions') && expandedLayerImages?.lesions ? (
                        <img
                          className="absolute inset-0 h-auto max-w-full xl:flex-1 object-scale-down lg:max-h-[75vh] opacity-50 contrast-125"
                          src={expandedLayerImages.lesions.url}
                          alt="Lesions overlay"
                        />
                      ) : null}
                    </picture>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                      {imagesFiltered?.map((image, index) => (
                        <img
                          key={image.url}
                          className={`xl:h-24 object-scale-down cursor-pointer ${
                            expanded === image.image ? 'ring-2 ring-primary' : ''
                          }`}
                          src={image.url}
                          alt={`Case img ${index + 1}`}
                          onClick={() => handleExpand(image.image)}
                        />
                      ))}
                    </div>
                  </div>
                  <ImageSegDataAccordionSelect
                    img={expanded ?? ''}
                    setSegmentationSelected={setSegmentationSelected}
                    segmentationSelected={segmentationSelected}
                    layerImages={expandedLayerImages}
                    loading={loadingLayers}
                    segmentationData={segmentationData}
                  />
                </div>
              </FetchStates>
            </div>
          </Overlay>
        </Portal>
      ) : null}
    </>
  );
}
